import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

// project-imports
import { getFormattedDate, getLocalDateTimeFields } from 'helpers/dates';
import { dispatch, useSelector } from 'store';
import { rowsPerPagePaginationAlt } from 'store/constant';
import { getAgentFollowUpsData } from 'store/slices/acmDashboard';
import { addFollowUpData, getFollowUpData, getMemberFollowupData, setLoadingState } from 'store/slices/memberPriority';
import { openSnackbar } from 'store/slices/snackbar';
import MainCard from 'ui-component/cards/MainCard';
import AutoCompleteInput from 'ui-component/extended/autocomplete';
import {
    DASHBOARD_VIEW,
    MODULE_ACCESS_MEMBER_PRIORITY_DASHBOARD_SIDE_NAV,
    followUpHeadCells,
    initialFollowupData,
    MEMBER_LIST_VIEW,
    VIEW_TYPE_MPD_VIEW
} from '../constant';
import useStyles from '../styles';

// material-ui
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useTheme from '@mui/material/styles/useTheme';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

// third-party
import { useFormik } from 'formik';
import * as yup from 'yup';

const validationSchema = yup.object({
    campaign: yup.object().nullable().required('Campaign is required.'),
    action: yup.object().nullable().required('Action is required.'),
    remarks: yup.string().required('Remarks is required.'),
    next_action_at: yup.date().nullable().required('Next Action Date is required.')
});

const TableHeading = () => (
    <>
        {followUpHeadCells.map((item) => (
            <TableCell align={item.align}>{item?.label}</TableCell>
        ))}
    </>
);

const MemberFollowUp = ({ viewType, handleDrawerOpen }) => {
    const theme = useTheme();
    const classes = useStyles();
    const memberRemarksTimeOutRef = useRef('');

    const [actionData, setActionData] = useState([]);
    const [campaignData, setCampaignData] = useState([]);

    const memberPriorityData = useSelector((state) => state?.memberPriority);
    const {
        selectedMember = {},
        selectedMemberData = {},
        isFollowupLoading = false,
        isRecentFollowUpLoading = false,
        memberFollowupData = {},
        followUpData = {}
    } = memberPriorityData || {};

    const {
        results = [],
        count = 0,
        next = null,
        previous = null,
        page_size: rowsPerPage = rowsPerPagePaginationAlt,
        currentPage = 0
    } = followUpData || {};

    const { code: memberCode = '' } = selectedMember || {};
    const { utilities = {} } = selectedMemberData || {};
    const { filters = [] } = utilities || {};

    const moduleAccessData = useSelector((state) => state.moduleAccess);
    const { moduleAccessMap = {} } = moduleAccessData || {};

    const acmData = useSelector((state) => state?.acmDashboard);
    const { agentFollowUpFilters = {} } = acmData;

    const fetchData = async () => {
        if (viewType === DASHBOARD_VIEW || viewType === MEMBER_LIST_VIEW) {
            const {
                campaign: {
                    campaign: { id: agentFollowUpId = null }
                }
            } = agentFollowUpFilters;
            const newAgentFollowUp =
                agentFollowUpId === 0 ? { next_action_at: agentFollowUpFilters?.next_action_at } : agentFollowUpFilters;
            await dispatch(getAgentFollowUpsData('', newAgentFollowUp));
            handleDrawerOpen();
        } else {
            dispatch(getFollowUpData(memberCode));
            dispatch(setLoadingState({ loadingField: 'isFollowupLoading', isLoading: false }));
        }
        await dispatch(
            openSnackbar({
                open: true,
                message: 'Remarks Updated',
                variant: 'alert',
                alert: {
                    color: 'success'
                },
                close: false
            })
        );
    };

    const formik = useFormik({
        initialValues: { ...memberFollowupData },
        enableReinitialize: true,
        validationSchema,
        onSubmit: (values, { resetForm }) => {
            const newValues = { ...values };
            const { campaign = {}, action = {} } = values || {};
            newValues.campaign = campaign?.id;
            newValues.action = action?.id;
            dispatch(getMemberFollowupData(initialFollowupData));
            dispatch(addFollowUpData(memberCode, newValues));
            if (viewType === VIEW_TYPE_MPD_VIEW) {
                dispatch(setLoadingState({ loadingField: 'isFollowupLoading', isLoading: true }));
            }
            setTimeout(() => {
                fetchData();
            }, 3000);
            resetForm();
        }
    });

    const { handleChange, setFieldValue } = formik;

    const handleFieldChange = (e, type) => {
        if (type === 'next_action_at') {
            setFieldValue('next_action_at', e);
            dispatch(getMemberFollowupData({ ...memberFollowupData, [type]: e }));
        } else {
            handleChange(e);
            if (memberRemarksTimeOutRef.current) {
                clearTimeout(memberRemarksTimeOutRef.current);
                memberRemarksTimeOutRef.current = null;
            } else {
                memberRemarksTimeOutRef.current = setTimeout(async () => {
                    await dispatch(getMemberFollowupData({ ...memberFollowupData, [type]: e.target.value }));
                }, 10);
            }
        }
    };

    const getFilterTypeOptions = (filters) => {
        // eslint-disable-next-line array-callback-return, consistent-return
        filters.forEach((filterTypeObj) => {
            const [firstKey] = Object.keys(filterTypeObj);
            if (firstKey === 'action') {
                setActionData([...filterTypeObj[firstKey]]);
            } else if (firstKey === 'campaign') {
                setCampaignData([...filterTypeObj[firstKey]]);
            }
        });
    };

    const handleChangePage = (event, nextPage) => {
        if (nextPage - currentPage > 0) {
            // next
            dispatch(getFollowUpData(memberCode, next, nextPage));
        } else if (nextPage - currentPage < 0) {
            // previous
            dispatch(getFollowUpData(memberCode, previous, nextPage));
        }
    };

    useEffect(() => {
        if (!filters || filters.length === 0) return;
        getFilterTypeOptions(filters);
    }, [filters]);

    useEffect(() => {
        if (!memberCode) return;
        dispatch(getFollowUpData(memberCode));
    }, [memberCode]);

    return (
        <>
            {isFollowupLoading ? (
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </Grid>
            ) : (
                <form onSubmit={formik.handleSubmit}>
                    <Grid container item direction="column" spacing={4} xs={12}>
                        <Grid container item spacing={3} xs={12} direction="column">
                            <Grid item xs={12}>
                                <Typography variant="subtitle1">Add Follow Up</Typography>
                            </Grid>
                            <Grid item spacing={4} container xs={12}>
                                <Grid item xs={4}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DesktopDatePicker
                                            inputFormat="dd/MM/yyyy"
                                            renderInput={(props) => (
                                                <TextField
                                                    fullWidth
                                                    {...props}
                                                    error={formik.touched.next_action_at && Boolean(formik.errors.next_action_at)}
                                                    helperText={formik.touched.next_action_at && formik.errors.next_action_at}
                                                />
                                            )}
                                            label="Next Follow up Date"
                                            disablePast
                                            value={formik.values.next_action_at}
                                            onChange={(date) => handleFieldChange(date, 'next_action_at')}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={4}>
                                    <AutoCompleteInput
                                        id="campaign"
                                        label="Campaign"
                                        options={campaignData}
                                        isMultiSelect={false}
                                        value={formik.values.campaign}
                                        error={formik.touched.campaign && Boolean(formik.errors.campaign)}
                                        helperText={formik.touched.campaign && formik.errors.campaign}
                                        onChange={(e, newValue) => {
                                            const tempFilterTypeObj = {};
                                            if (newValue) {
                                                tempFilterTypeObj[newValue?.id] = newValue;
                                            }

                                            formik.setFieldValue('campaign', newValue);
                                            dispatch(getMemberFollowupData({ ...memberFollowupData, campaign: newValue }));
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <AutoCompleteInput
                                        id="action"
                                        label="Action"
                                        options={actionData}
                                        isMultiSelect={false}
                                        value={formik.values.action}
                                        error={formik.touched.action && Boolean(formik.errors.action)}
                                        helperText={formik.touched.action && formik.errors.action}
                                        onChange={(event, newValue) => {
                                            const tempFilterTypeObj = {};
                                            if (newValue) {
                                                tempFilterTypeObj[newValue?.id] = newValue;
                                            }

                                            formik.setFieldValue('action', newValue);
                                            dispatch(getMemberFollowupData({ ...memberFollowupData, action: newValue }));
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        id="remarks"
                                        name="remarks"
                                        label="Remarks"
                                        multiline
                                        rows={3}
                                        onChange={(e) => handleFieldChange(e, 'remarks')}
                                        value={formik.values.remarks}
                                        error={formik.touched.remarks && Boolean(formik.errors.remarks)}
                                        helperText={formik.touched.remarks && formik.errors.remarks}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item container xs={12} justifyContent="center">
                            <Button
                                variant="contained"
                                type="submit"
                                disabled={
                                    !moduleAccessMap[MODULE_ACCESS_MEMBER_PRIORITY_DASHBOARD_SIDE_NAV]?.is_active ||
                                    moduleAccessMap[MODULE_ACCESS_MEMBER_PRIORITY_DASHBOARD_SIDE_NAV]?.action.toLowerCase() === 'view'
                                }
                                sx={{ width: '25%' }}
                            >
                                Save
                            </Button>
                        </Grid>
                        <Grid container item spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1">Recent Follow up</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <MainCard content={false}>
                                    <TableContainer>
                                        <Table sx={{ minWidth: 350 }} aria-label="simple table">
                                            <TableHead>
                                                <TableRow className={classes.primaryBackground}>
                                                    <TableHeading />
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {isRecentFollowUpLoading ? (
                                                    <TableRow>
                                                        <TableCell colSpan={6} align="center">
                                                            <CircularProgress />
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    results.map((activity) => {
                                                        const {
                                                            campaign = {},
                                                            remarks = '',
                                                            action = {},
                                                            next_action_at: nextActionAt = '',
                                                            created_at: lastReachOut = '',
                                                            done_by: doneBy = {}
                                                        } = activity || {};
                                                        const { name: campaignName = '' } = campaign || {};
                                                        const { name: actionName = '' } = action || {};

                                                        const { hours24: nextActionAtHrs = '', minutes: nextActionAtMin = '' } =
                                                            getLocalDateTimeFields(nextActionAt) || {};

                                                        const { hours24: lastReachOutHrs = '', minutes: lastReachOutMin = '' } =
                                                            getLocalDateTimeFields(lastReachOut) || {};

                                                        return (
                                                            <TableRow hover key="followUpTable">
                                                                <TableCell sx={{ minWidth: '150px' }}>{campaignName || '-'}</TableCell>
                                                                <TableCell sx={{ minWidth: '150px' }}>{actionName || '-'}</TableCell>
                                                                <TableCell
                                                                    sx={{
                                                                        minWidth: '154px'
                                                                    }}
                                                                    component="th"
                                                                >
                                                                    {lastReachOut ? (
                                                                        <>
                                                                            <Typography>{`${getFormattedDate(lastReachOut)}`}</Typography>
                                                                            <Typography
                                                                                variant="h6"
                                                                                sx={{ color: theme.palette.grey[500], fontWeight: 400 }}
                                                                            >
                                                                                {`${lastReachOutHrs}:${lastReachOutMin}`}
                                                                            </Typography>
                                                                        </>
                                                                    ) : (
                                                                        '-'
                                                                    )}
                                                                </TableCell>
                                                                <TableCell sx={{ minWidth: '140px' }}>{remarks || '-'}</TableCell>
                                                                <TableCell sx={{ minWidth: '140px' }}>{doneBy?.username || '-'}</TableCell>
                                                                <TableCell
                                                                    sx={{
                                                                        minWidth: '120px'
                                                                    }}
                                                                    component="th"
                                                                >
                                                                    {nextActionAt ? (
                                                                        <>
                                                                            <Typography>{`${getFormattedDate(nextActionAt)}`}</Typography>
                                                                            <Typography
                                                                                variant="h6"
                                                                                sx={{ color: theme.palette.grey[500], fontWeight: 400 }}
                                                                            >
                                                                                {`${nextActionAtHrs}:${nextActionAtMin}`}
                                                                            </Typography>
                                                                        </>
                                                                    ) : (
                                                                        '-'
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <TablePagination
                                        rowsPerPageOptions={[rowsPerPage]}
                                        component="div"
                                        rowsPerPage={rowsPerPage}
                                        page={currentPage}
                                        count={parseInt(count, 10)}
                                        backIconButtonProps={{
                                            'aria-label': 'previous page'
                                        }}
                                        nextIconButtonProps={{
                                            'aria-label': 'next page'
                                        }}
                                        onPageChange={handleChangePage}
                                        labelDisplayedRows={({ count }) =>
                                            `Page ${currentPage + 1} of ${Math.ceil(parseInt(count, 10) / rowsPerPage)}`
                                        }
                                        sx={{ '& .MuiTablePagination-toolbar': { padding: 0 } }}
                                    />
                                </MainCard>
                            </Grid>
                        </Grid>
                    </Grid>
                </form>
            )}
        </>
    );
};

MemberFollowUp.propTypes = {
    handleDrawerOpen: PropTypes.func,
    viewType: PropTypes.bool
};

export default MemberFollowUp;
